import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import s from '../OpsMapDashboard.module.scss'; // Reuse styles

const LegendDriverIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5-8.5l1.96 2.5H17V9.5h2.5zM18 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const LegendBookingIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const LegendPushpinIcon = ({ color }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" style={{ marginRight: '8px', flexShrink: 0 }}>
        <path d="M16 9V4.5C16 3.12 14.88 2 13.5 2h-3C9.12 2 8 3.12 8 4.5V9c-2.21 0-4 1.79-4 4s1.79 4 4 4h3v4h2v-4h3c2.21 0 4-1.79 4-4s-1.79-4-4-4z" fill={color} stroke="#1a1a1a" strokeWidth="1" />
    </svg>
);

const formatVehicleClassLabel = (vehicleClass) => {
    const raw = String(vehicleClass || '').trim();
    if (!raw || raw === 'N/A') return '';
    const match = raw.match(/class\s*(\d+)/i);
    if (match) return `Class ${match[1]}`;
    if (/^\d+$/.test(raw)) return `Class ${raw}`;
    return raw.replace(/^class/i, 'Class ');
};

const ClusterDriverCard = ({ driver, isSelected, onClusterDriverClick, onViewVehicleDetails }) => {
    const name = (driver.driver_name || `Driver #${driver.driver_id || driver.id || ''}`).toUpperCase();
    const vehicleId = driver.vehicle_id;
    const registration = driver.vehicle_registration && driver.vehicle_registration !== 'N/A'
        ? driver.vehicle_registration
        : '';
    const status = (driver.driver_status || 'unknown').toLowerCase();
    const classLabel = formatVehicleClassLabel(driver.vehicle_class);
    const model = driver.vehicle_model && driver.vehicle_model !== 'N/A' ? driver.vehicle_model : '';
    const classModel = [classLabel, model].filter(Boolean).join(' - ');
    const statusClassLine = [status, classModel].filter(Boolean).join(' ');

    return (
        <div
            className={`${s.clusterVehicleCard} ${isSelected ? s.clusterVehicleCardActive : ''}`}
            role={onClusterDriverClick ? 'button' : undefined}
            tabIndex={onClusterDriverClick ? 0 : undefined}
            onClick={() => onClusterDriverClick?.(driver)}
            onKeyDown={(e) => {
                if (onClusterDriverClick && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onClusterDriverClick(driver);
                }
            }}
        >
            <div className={s.clusterVehicleName}>
                {name}{vehicleId ? `(${vehicleId})` : ''}
            </div>
            {registration && (
                <div className={s.clusterVehicleReg}>{registration}</div>
            )}
            {statusClassLine && (
                <div className={s.clusterVehicleMeta}>{statusClassLine}</div>
            )}
            {driver.phone && (
                <div className={s.clusterVehiclePhone}>{driver.phone}</div>
            )}
            {vehicleId && (
                <button
                    type="button"
                    onClick={(e) => {
                        e.stopPropagation();
                        onViewVehicleDetails?.(driver);
                    }}
                    style={{
                        marginTop: '6px',
                        background: '#2563eb',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '3px 8px',
                        fontSize: '11px',
                        fontWeight: 600,
                        cursor: 'pointer',
                    }}
                >
                    View Details
                </button>
            )}
        </div>
    );
};

const ToggleSwitch = ({ checked, onChange = () => { }, id, disabled = false }) => {
    const trackStyle = {
        position: 'relative',
        width: '34px',
        height: '18px',
        borderRadius: '9px',
        background: disabled ? '#f3f4f6' : checked ? '#3b82f6' : '#d1d5db',
        cursor: disabled ? 'not-allowed' : 'pointer',
        transition: 'background 0.2s',
        flexShrink: 0,
        opacity: disabled ? 0.6 : 1,
    };
    const thumbStyle = {
        position: 'absolute',
        top: '2px',
        left: checked ? '18px' : '2px',
        width: '14px',
        height: '14px',
        borderRadius: '50%',
        background: '#fff',
        boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        transition: 'left 0.2s',
    };

    return (
        <div
            role="switch"
            aria-checked={checked}
            aria-disabled={disabled}
            id={id}
            style={trackStyle}
            onClick={() => !disabled && onChange(!checked)}
        >
            <div style={thumbStyle} />
        </div>
    );
};

const OpsMapStats = ({
    driverCount,
    bookingCount,
    timeWindow,
    bookings = [],
    driversList = [],
    clusterDrivers = null,
    selectedDriver = null,
    onClearClusterDrivers,
    onClusterDriverClick,
    onViewVehicleDetails,
    onDriverClick,
    onBookingClick,
    driverStatusFilter,
    setDriverStatusFilter,
    onModifyServiceableRegions,
    onModifyAddresses,
    pincodeAnalysis = null,
    onClearPincodeAnalysis,
    // Layer Panel Props
    showServiceZone,
    setShowServiceZone,
    showHotzones,
    setShowHotzones,
    showRealtimeHotzones,
    setShowRealtimeHotzones,
    showHistoricalHotzones,
    setShowHistoricalHotzones,
    showLeadHeatmap,
    setShowLeadHeatmap,
    leadHeatmapStatuses = [],
    setLeadHeatmapStatuses,
    leadTonnageMode = 'all',
    setLeadTonnageMode,
    leadTonnageMin = '',
    setLeadTonnageMin,
    leadTonnageMax = '',
    setLeadTonnageMax,
    leadHeatmap = null,
    leadHeatmapLoading = false,
    filteredLeadDestinationCount = 0,
    showDrivers,
    setShowDrivers,
    showRegisteredVehicles,
    setShowRegisteredVehicles,
    showTransportersOnly,
    setShowTransportersOnly,
    vehicleClassOptions = [],
    vehicleClassFilter = [],
    setVehicleClassFilter,
    showBookings,
    setShowBookings,
    showCriticalOnly,
    setShowCriticalOnly,
    showCompletedBookings,
    setShowCompletedBookings,
}) => {
    const toggleVehicleClass = (classNum) => {
        if (!setVehicleClassFilter) return;
        setVehicleClassFilter((prev) => {
            const next = new Set(prev || []);
            if (next.has(classNum)) {
                next.delete(classNum);
            } else {
                next.add(classNum);
            }
            return Array.from(next).sort((a, b) => a - b);
        });
    };

    const clearVehicleClassFilter = () => {
        if (!setVehicleClassFilter) return;
        setVehicleClassFilter([]);
    };
    // Which detail panel is expanded: 'drivers', 'bookings', or null
    const [expandedPanel, setExpandedPanel] = useState(null);

    // Filtering states
    const [driverSearch, setDriverSearch] = useState('');
    const [bookingSearch, setBookingSearch] = useState('');
    const [debouncedBookingSearch, setDebouncedBookingSearch] = useState('');

    // Debounce booking search (300ms delay)
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedBookingSearch(bookingSearch), 300);
        return () => clearTimeout(timer);
    }, [bookingSearch]);

    // Determine time label (presets + custom Nh)
    const getTimeLabel = (w) => {
        if (w === '1h') return 'Next 1 Hour';
        if (w === '3h') return 'Next 3 Hours';
        if (w === '6h') return 'Next 6 Hours';
        if (w === 'today') return 'All Today';
        const m = (typeof w === 'string' && w) ? w.match(/^(\d+)h$/) : null;
        return m ? `Next ${m[1]} Hours` : (w || '');
    };

    // Format booking time
    const formatTime = (dateString) => {
        const date = new Date(dateString);
        return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    };

    // Driver status mapping for colors
    const getStatusColor = (status) => {
        const colors = {
            active: 'primary',
            available: 'success',
            inactive: 'warning',
            default: 'secondary'
        };
        return colors[status] || colors.default;
    };

    // Toggle panel expansion
    const togglePanel = (panelName) => {
        setExpandedPanel(prev => prev === panelName ? null : panelName);
    };

    // Memoized filtered lists
    const filteredDrivers = React.useMemo(() => {
        return driversList.filter(driver => {
            const matchesSearch = (driver.driver_name?.toString() || "").toLowerCase().includes(driverSearch.toLowerCase()) ||
                (driver.vehicle_registration?.toString() || "").toLowerCase().includes(driverSearch.toLowerCase()) ||
                (driver.vehicle_id?.toString() || "").toLowerCase().includes(driverSearch.toLowerCase());

            let matchesStatus = false;
            if (driverStatusFilter === 'all') matchesStatus = true;
            else if (driverStatusFilter === 'registered') matchesStatus = driver.type === 'registered';
            else if (driverStatusFilter === 'active') matchesStatus = driver.type === 'live' && driver.driver_status === 'active';
            else if (driverStatusFilter === 'available') matchesStatus = driver.type === 'live' && driver.driver_status === 'available';
            else matchesStatus = driver.driver_status === driverStatusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [driversList, driverSearch, driverStatusFilter]);

    const filteredBookings = React.useMemo(() => {
        // Step 1: Filter by search
        const searched = bookings.filter(booking => {
            return (booking.id?.toString() || "").toLowerCase().includes(debouncedBookingSearch.toLowerCase()) ||
                (booking.pickup_address?.toString() || "").toLowerCase().includes(debouncedBookingSearch.toLowerCase()) ||
                (booking.drop_address?.toString() || "").toLowerCase().includes(debouncedBookingSearch.toLowerCase());
        });

        // Step 2: Sort by urgency (Unassigned → Delayed → Pending → Assigned → Completed)
        const urgencyOrder = { Unassigned: 0, Delayed: 1, Pending: 2, Assigned: 3, Completed: 4 };
        const getEffectiveStatus = (b) => {
            if (b.is_unassigned) return 'Unassigned';
            if (b.escalation) return 'Delayed';
            return b.state || 'Pending';
        };

        return searched.sort((a, b) => {
            const statusA = getEffectiveStatus(a);
            const statusB = getEffectiveStatus(b);
            const orderDiff = (urgencyOrder[statusA] ?? 99) - (urgencyOrder[statusB] ?? 99);
            if (orderDiff !== 0) return orderDiff;
            // Within same status, sort by pickup time (earliest/most overdue first)
            return new Date(a.pickup_time) - new Date(b.pickup_time);
        });
    }, [bookings, debouncedBookingSearch]);

    return (
        <div className="stats-container">
            {pincodeAnalysis && pincodeAnalysis.is_city_wide && (
                <div className={s.statCard} style={{ borderLeft: '4px solid #ef4444', background: '#ffffff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h5 className="mb-0" style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>
                                <i className="fa fa-map text-danger mr-2" />
                                Coverage: {pincodeAnalysis.city}
                            </h5>
                            <small className="text-muted">City-Wide Pincodes ({pincodeAnalysis.pincodes?.length || 0})</small>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 d-flex align-items-center"
                            onClick={onClearPincodeAnalysis}
                            title="Clear selection"
                            style={{ textDecoration: 'none' }}
                        >
                            <i className="fa fa-times-circle" style={{ fontSize: '18px' }} />
                        </button>
                    </div>
                    <p className="small text-muted mb-0" style={{ lineHeight: '1.4' }}>
                        Click on any <strong className="text-danger">red triangle marker</strong> on the map to analyze onboarding status and missing classes for that specific pincode.
                    </p>
                </div>
            )}

            {pincodeAnalysis && !pincodeAnalysis.is_city_wide && (
                <div className={s.statCard} style={{ borderLeft: '4px solid #3b82f6', background: '#ffffff', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                        <div>
                            <h5 className="mb-0" style={{ fontSize: '15px', fontWeight: 'bold', color: '#1e293b' }}>
                                <i className="fa fa-map-pin text-primary mr-2" />
                                Coverage: {pincodeAnalysis.pincode} {pincodeAnalysis.city ? `(${pincodeAnalysis.city})` : ''}
                            </h5>
                            <small className="text-muted">Onboarding Status</small>
                        </div>
                        <button
                            type="button"
                            className="btn btn-sm btn-link text-danger p-0 d-flex align-items-center"
                            onClick={onClearPincodeAnalysis}
                            title="Clear selection"
                            style={{ textDecoration: 'none' }}
                        >
                            <i className="fa fa-times-circle" style={{ fontSize: '18px' }} />
                        </button>
                    </div>

                    {/* Classes Status */}
                    <div className="mb-3">
                        <div className="mb-2">
                            <span className="small text-muted font-weight-bold d-block mb-1" style={{ fontSize: '11px' }}>Onboarded Classes:</span>
                            {pincodeAnalysis.present_classes && pincodeAnalysis.present_classes.length > 0 ? (
                                <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                    {pincodeAnalysis.present_classes.map(c => (
                                        <span key={c} className="badge badge-success px-2 py-1" style={{ fontSize: '10px', background: '#10B981' }}>
                                            Class {c}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-muted small italic">None</span>
                            )}
                        </div>

                        <div>
                            <span className="small text-muted font-weight-bold d-block mb-1" style={{ fontSize: '11px' }}>Missing / Yet to Onboard:</span>
                            {pincodeAnalysis.missing_classes && pincodeAnalysis.missing_classes.length > 0 ? (
                                <div className="d-flex flex-wrap" style={{ gap: '4px' }}>
                                    {pincodeAnalysis.missing_classes.map(c => (
                                        <span key={c} className="badge px-2 py-1" style={{ fontSize: '10px', color: '#ef4444', border: '1px solid #fecaca', background: '#fef2f2' }}>
                                            Class {c}
                                        </span>
                                    ))}
                                </div>
                            ) : (
                                <span className="text-success small font-weight-bold"><i className="fa fa-check-circle mr-1" />Fully covered!</span>
                            )}
                        </div>
                    </div>

                    {/* Vehicles list in pincode */}
                    <div style={{ maxHeight: '150px', overflowY: 'auto', borderTop: '1px dashed #e2e8f0', paddingTop: '8px' }}>
                        <span className="small text-muted font-weight-bold d-block mb-1" style={{ fontSize: '11px' }}>
                            Vehicles in Area ({pincodeAnalysis.vehicles?.length || 0}):
                        </span>
                        {pincodeAnalysis.vehicles && pincodeAnalysis.vehicles.length > 0 ? (
                            <div className="d-flex flex-col" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                {pincodeAnalysis.vehicles.map(v => (
                                    <div key={v.id} className="p-2 rounded small d-flex justify-content-between align-items-center" style={{ background: '#f8fafc', fontSize: '11px', border: '1px solid #e2e8f0' }}>
                                        <span className="font-weight-bold text-dark text-truncate" style={{ maxWidth: '65%' }}>
                                            {v.registration_no || `ID: ${v.id}`}
                                        </span>
                                        <span className="badge badge-secondary" style={{ background: '#64748b' }}>
                                            Class {v.class || 'N/A'}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <span className="text-muted small italic">No active vehicles in this pincode</span>
                        )}
                    </div>
                </div>
            )}

            {clusterDrivers && clusterDrivers.length > 0 && (
                <div className={s.clusterPanel}>
                    <div className={s.detailHeader}>
                        <h6 className="mb-0 font-weight-bold">
                            Map cluster ({clusterDrivers.length} vehicle{clusterDrivers.length !== 1 ? 's' : ''})
                        </h6>
                        <button
                            type="button"
                            className="btn btn-sm btn-outline-secondary"
                            onClick={onClearClusterDrivers}
                            title="Clear cluster selection"
                        >
                            <i className="fa fa-times" />
                        </button>
                    </div>
                    <div className={s.clusterVehicleList}>
                        {clusterDrivers.map((driver, index) => {
                            const driverId = driver.driver_id || driver.id;
                            const selectedId = selectedDriver?.driver_id || selectedDriver?.id;
                            const isSelected = selectedId != null && String(driverId) === String(selectedId);
                            return (
                                <ClusterDriverCard
                                    key={driver.driver_id || driver.id || index}
                                    driver={driver}
                                    isSelected={isSelected}
                                    onClusterDriverClick={onClusterDriverClick}
                                    onViewVehicleDetails={onViewVehicleDetails}
                                />
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Total Drivers (Supply) Card */}
            <div
                className={`${s.statCard} ${expandedPanel === 'drivers' ? s.statCardActive : ''}`}
                style={{ cursor: driversList.length > 0 ? 'pointer' : 'default' }}
                onClick={() => driversList.length > 0 && togglePanel('drivers')}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5>Total Vehicles</h5>
                        <div className={s.value}>{driverCount}</div>
                        <div className={s.subtext}>Registered Vehicles</div>
                    </div>
                    <div className="text-primary">
                        <i className="fa fa-truck fa-2x opacity-50" />
                    </div>
                </div>
                {driversList.length > 0 && (
                    <div className="mt-2">
                        {expandedPanel !== 'drivers' && (
                            <div className="text-muted small">
                                <i className="fa fa-info-circle mr-1"></i>
                                Click to see all drivers
                            </div>
                        )}
                        <div className="text-center mt-1">
                            <i className="fa fa-chevron-down" style={{
                                fontSize: '14px',
                                color: '#9CA3AF',
                                transition: 'transform 0.2s',
                                transform: expandedPanel === 'drivers' ? 'rotate(180deg)' : 'rotate(0deg)'
                            }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Inline Driver Details Panel */}
            {expandedPanel === 'drivers' && (
                <div className={s.detailPanel}>
                    <div className={s.detailHeader}>
                        <h6 className="mb-0 font-weight-bold">Drivers ({filteredDrivers.length}/{driversList.length})</h6>
                        <div className="d-flex" style={{ gap: '6px' }}>
                            <button
                                className="btn btn-sm btn-light"
                                onClick={(e) => { e.stopPropagation(); setDriverSearch(''); setDriverStatusFilter('all'); }}
                            >
                                Clear
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={(e) => { e.stopPropagation(); setExpandedPanel(null); }}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm mb-2"
                            placeholder="Search name or vehicle..."
                            value={driverSearch}
                            onChange={(e) => setDriverSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                        <div className="btn-group btn-group-sm d-flex" style={{ flexWrap: 'wrap' }}>
                            <button className={`btn btn-outline-secondary ${driverStatusFilter === 'all' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setDriverStatusFilter('all') }}>All</button>
                            <button className={`btn btn-outline-success ${driverStatusFilter === 'available' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setDriverStatusFilter('available') }}>Avail</button>
                            <button className={`btn btn-outline-primary ${driverStatusFilter === 'active' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setDriverStatusFilter('active') }}>Active</button>
                            <button className={`btn btn-outline-dark ${driverStatusFilter === 'registered' ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setDriverStatusFilter('registered') }}>Registered</button>
                        </div>
                    </div>

                    <div className={s.detailItemList}>
                        {filteredDrivers.length === 0 ? (
                            <div className="text-center py-3 text-muted small">No drivers match filters</div>
                        ) : (
                            filteredDrivers.map((driver, index) => (
                                <div
                                    key={driver.id || driver.driver_id || index}
                                    className={s.detailItem}
                                    role="button"
                                    tabIndex={0}
                                    style={{
                                        cursor: onDriverClick ? 'pointer' : undefined,
                                        ...(driver.is_transporter ? {
                                            borderLeft: '4px solid #db2777',
                                            backgroundColor: '#fdf2f8'
                                        } : {})
                                    }}
                                    onClick={(e) => { e.stopPropagation(); onDriverClick?.(driver); }}
                                >
                                    <div className="d-flex justify-content-between align-items-center mb-1">
                                        <strong className="text-dark text-truncate mr-2" style={{ minWidth: 0 }}>
                                            {driver.driver_name || `Driver #${driver.driver_id}`}
                                            {driver.vehicle_id && <span className="ml-1" style={{ fontWeight: 800 }}>({driver.vehicle_id})</span>}
                                        </strong>
                                        <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                                            {driver.driver_status === 'registered' && driver.current_step && driver.current_step !== '-' && (
                                                <span style={{
                                                    background: driver.current_step === 'completion' ? '#dcfce7' : '#fef08a',
                                                    color: driver.current_step === 'completion' ? '#166534' : '#854d0e',
                                                    padding: '3px 6px',
                                                    borderRadius: '4px',
                                                    fontSize: '11px',
                                                    fontWeight: '600',
                                                    textTransform: 'capitalize',
                                                    whiteSpace: 'nowrap',
                                                    flexShrink: 0
                                                }}>
                                                    {driver.current_step}
                                                </span>
                                            )}
                                            <span className={`badge badge-${getStatusColor(driver.driver_status)}`}>
                                                {driver.driver_status || 'offline'}
                                            </span>
                                            {driver.is_transporter && (
                                                <span className="badge ml-1" style={{
                                                    fontSize: '11px',
                                                    backgroundColor: '#db2777',
                                                    color: '#ffffff',
                                                    fontWeight: '600'
                                                }}>
                                                    Transporter
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="small text-muted">
                                        <div className="text-truncate" style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500, marginBottom: '2px' }}>
                                            <i className="fa fa-truck mr-1" style={{ width: '12px', textAlign: 'center' }}></i>
                                            <span style={{ textTransform: 'capitalize' }}>
                                                {String(driver.vehicle_class || 'N/A').replace('class', 'Class ')}
                                            </span>
                                            {driver.vehicle_model && driver.vehicle_model !== 'N/A' ? ` - ${driver.vehicle_model}` : ''}
                                        </div>
                                        {driver.driver_status !== 'registered' && driver.vehicle_registration && driver.vehicle_registration !== 'N/A' && (
                                            <div className="text-truncate" style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500, marginBottom: '2px' }}>
                                                <i className="fa fa-id-card-o mr-1" style={{ width: '12px', textAlign: 'center' }}></i> {driver.vehicle_registration}
                                            </div>
                                        )}
                                        {driver.phone && (
                                            <div className="text-truncate" style={{ fontSize: '11px', color: '#6b7280', fontWeight: 500 }}>
                                                <i className="fa fa-phone mr-1" style={{ width: '12px', textAlign: 'center' }}></i> {driver.phone}
                                            </div>
                                        )}
                                        {driver.is_transporter && driver.transport_routes && driver.transport_routes.length > 0 && (
                                            <div style={{
                                                marginTop: '6px',
                                                fontSize: '11px',
                                                color: '#374151',
                                                backgroundColor: '#fdf2f8',
                                                border: '1px solid #fbcfe8',
                                                padding: '6px',
                                                borderRadius: '6px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: '4px'
                                            }}>
                                                <div style={{ fontWeight: 700, color: '#db2777', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                     <span>📍 Routes:</span>
                                                </div>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '2px', paddingLeft: '4px' }}>
                                                    {driver.transport_routes.map((r, idx) => {
                                                        const fromRaw = (r?.from?.address || '').trim();
                                                        const toRaw = (r?.to?.address || '').trim();
                                                        const fromLabel = fromRaw.split(',')[0].trim() || fromRaw || 'Any';
                                                        const toLabel = toRaw.split(',')[0].trim() || toRaw || 'Any';
                                                        return (
                                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500, color: '#4b5563' }}>
                                                                <span>{fromLabel}</span>
                                                                <span style={{ color: '#db2777' }}>→</span>
                                                                <span>{toLabel}</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            {/* Upcoming Bookings (Demand) Card */}
            <div
                className={`${s.statCard} ${expandedPanel === 'bookings' ? s.statCardActive : ''}`}
                style={{ cursor: bookings.length > 0 ? 'pointer' : 'default' }}
                onClick={() => bookings.length > 0 && togglePanel('bookings')}
            >
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5>Upcoming Bookings</h5>
                        <div className={s.value}>{bookingCount}</div>
                        <div className={s.subtext}>Demand ({getTimeLabel(timeWindow)})</div>
                    </div>
                    <div className="text-danger">
                        <i className="fa fa-map-marker fa-2x opacity-50" />
                    </div>
                </div>
                {bookings.length > 0 && (
                    <div className="mt-2">
                        {expandedPanel !== 'bookings' && (
                            <div className="text-muted small">
                                <i className="fa fa-info-circle mr-1"></i>
                                Click to see all bookings
                            </div>
                        )}
                        <div className="text-center mt-1">
                            <i className="fa fa-chevron-down" style={{
                                fontSize: '14px',
                                color: '#9CA3AF',
                                transition: 'transform 0.2s',
                                transform: expandedPanel === 'bookings' ? 'rotate(180deg)' : 'rotate(0deg)'
                            }} />
                        </div>
                    </div>
                )}
            </div>

            {/* Inline Booking Details Panel */}
            {expandedPanel === 'bookings' && (
                <div className={s.detailPanel}>
                    <div className={s.detailHeader}>
                        <h6 className="mb-0 font-weight-bold">Bookings ({filteredBookings.length}/{bookings.length})</h6>
                        <div className="d-flex" style={{ gap: '6px' }}>
                            <button
                                className="btn btn-sm btn-light"
                                onClick={(e) => { e.stopPropagation(); setBookingSearch(''); }}
                            >
                                Clear
                            </button>
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={(e) => { e.stopPropagation(); setExpandedPanel(null); }}
                            >
                                <i className="fa fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {/* Search */}
                    <div className="mb-2">
                        <input
                            type="text"
                            className="form-control form-control-sm"
                            placeholder="Search Booking ID or address..."
                            value={bookingSearch}
                            onChange={(e) => setBookingSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>

                    <div className={s.detailItemList}>
                        {filteredBookings.length === 0 ? (
                            <div className="text-center py-3 text-muted small">No bookings match search</div>
                        ) : (
                            filteredBookings.map((booking, index) => {
                                const now = new Date();
                                const pickupTime = new Date(booking.pickup_time);
                                const isPastPickup = booking.state === 'Pending' && pickupTime < now;
                                const isToday = pickupTime.toDateString() === now.toDateString();
                                const isOverdue = isPastPickup && isToday;
                                const isHistoricalUnassigned = isPastPickup && !isToday;
                                const displayState = isOverdue ? 'Overdue' : isHistoricalUnassigned ? 'Unassigned' : booking.state;

                                return (
                                    <div
                                        key={booking.id}
                                        className={s.detailItem}
                                        role="button"
                                        tabIndex={0}
                                        style={onBookingClick ? { cursor: 'pointer' } : undefined}
                                        onClick={(e) => { e.stopPropagation(); onBookingClick?.(booking); }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start mb-1">
                                            <strong className="text-primary text-truncate mr-2" style={{ minWidth: 0 }}>
                                                #{booking.id}
                                            </strong>
                                            <div className="d-flex flex-nowrap" style={{ gap: '4px' }}>
                                                <span
                                                    className="badge"
                                                    style={{
                                                        backgroundColor: booking.is_unassigned || isHistoricalUnassigned
                                                            ? '#EF4444'
                                                            : booking.escalation || isOverdue
                                                                ? '#A855F7'
                                                                : displayState === 'Completed'
                                                                    ? '#10B981'
                                                                    : displayState === 'Pending'
                                                                        ? '#FFFFFF'
                                                                        : '#3B82F6',
                                                        color: (!booking.is_unassigned && !booking.escalation && displayState === 'Pending') ? '#000' : '#fff',
                                                        border: (!booking.is_unassigned && !booking.escalation && displayState === 'Pending') ? '1px solid #ccc' : 'none'
                                                    }}
                                                >
                                                    {booking.is_unassigned
                                                        ? 'Unassigned'
                                                        : booking.escalation
                                                            ? (booking.escalation.escalation_type === 'pickup_delay' ? 'Pickup Delay'
                                                                : booking.escalation.escalation_type === 'destination_delay' ? 'Drop Delay'
                                                                    : booking.escalation.escalation_type === 'completion_delay' ? 'Completion Delay'
                                                                        : 'Delayed')
                                                            : displayState}
                                                </span>
                                                <span className="badge badge-light">{booking.vehicle_type}</span>
                                            </div>
                                        </div>
                                        <div className="small text-muted mb-1">
                                            <i className="fa fa-clock-o mr-1"></i>
                                            {formatTime(booking.pickup_time)}
                                        </div>
                                        <div className="small text-muted" style={{ wordBreak: 'break-word' }}>
                                            <div className="mb-1">
                                                <i className="fa fa-arrow-circle-up text-success mr-1"></i>
                                                <strong>From:</strong> {booking.pickup_address || booking.pickup_location}
                                            </div>
                                            <div>
                                                <i className="fa fa-arrow-circle-down text-danger mr-1"></i>
                                                <strong>To:</strong> {booking.drop_address || booking.drop_location}
                                            </div>
                                        </div>
                                    </div>
                                )
                            })
                        )}
                    </div >
                </div >
            )
            }

            {/* Map Layers card */}
            <div className={s.statCard}>
                <h5 className="mb-3" style={{ fontSize: '0.85rem', fontWeight: 700, color: 'var(--bs-secondary-color, #555)', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Map Layers / Controls
                </h5>

                {/* Serviceable Zone */}
                <div className="d-flex align-items-center justify-content-between py-2">
                    <label
                        style={{ fontSize: '13px', color: 'var(--bs-body-color, #333)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                        htmlFor="lp-serviceZone"
                        onClick={() => setShowServiceZone(v => !v)}
                    >
                        🟢 Serviceable Zone
                    </label>
                    <ToggleSwitch
                        checked={showServiceZone}
                        onChange={setShowServiceZone}
                        id="lp-serviceZone"
                    />
                </div>

                <div style={{ height: '1px', background: 'var(--bs-border-color, #f0f0f0)', margin: '6px 0' }} />

                {/* Hotzone Areas */}
                <div className="d-flex align-items-center justify-content-between py-2">
                    <label
                        style={{ fontSize: '13px', color: 'var(--bs-body-color, #333)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                        htmlFor="lp-hotzones"
                        onClick={() => setShowHotzones(v => !v)}
                    >
                        🔥 Hotzone Areas
                    </label>
                    <ToggleSwitch
                        checked={showHotzones}
                        onChange={setShowHotzones}
                        id="lp-hotzones"
                    />
                </div>

                {/* Hotzone sub-toggles */}
                {showHotzones && (
                    <div style={{ paddingLeft: '18px', borderLeft: '2px solid var(--bs-border-color, #eee)', marginLeft: '4px', marginTop: '2px', marginBottom: '2px' }}>
                        <div className="d-flex align-items-center justify-content-between py-1">
                            <label
                                style={{ fontSize: '12px', color: 'var(--bs-secondary-color, #666)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                                htmlFor="lp-realtimeHz"
                                onClick={() => setShowRealtimeHotzones(v => !v)}
                            >
                                🔴 Today's Demand
                            </label>
                            <ToggleSwitch
                                checked={showRealtimeHotzones}
                                onChange={setShowRealtimeHotzones}
                                id="lp-realtimeHz"
                            />
                        </div>
                        <div className="d-flex align-items-center justify-content-between py-1">
                            <label
                                style={{ fontSize: '12px', color: 'var(--bs-secondary-color, #666)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                                htmlFor="lp-historicalHz"
                                onClick={() => setShowHistoricalHotzones(v => !v)}
                            >
                                🟡 Vehicle Recommendation
                            </label>
                            <ToggleSwitch
                                checked={showHistoricalHotzones}
                                onChange={setShowHistoricalHotzones}
                                id="lp-historicalHz"
                            />
                        </div>
                    </div>
                )}

                <div style={{ height: '1px', background: 'var(--bs-border-color, #f0f0f0)', margin: '6px 0' }} />

                <div className="d-flex align-items-center justify-content-between py-2">
                    <label
                        style={{ fontSize: '13px', color: 'var(--bs-body-color, #333)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                        htmlFor="lp-leadHeat"
                        onClick={() => setShowLeadHeatmap?.(v => !v)}
                    >
                        Long-trip lead demand
                    </label>
                    <ToggleSwitch
                        checked={!!showLeadHeatmap}
                        onChange={setShowLeadHeatmap}
                        id="lp-leadHeat"
                    />
                </div>
                {showLeadHeatmap && (
                    <div className={s.leadHeatFilters}>
                        <div className={s.leadHeatMeta}>
                            {leadHeatmapLoading ? 'Loading 90-day leads…' : (
                                <>
                                    {leadHeatmap?.included_leads ?? 0} coast-crossing leads
                                    {' · '}
                                    {filteredLeadDestinationCount} destinations
                                </>
                            )}
                        </div>
                        <div className={s.leadHeatLabel}>Lead status</div>
                        <div className={s.leadHeatStatusRow}>
                            {['new', 'contacted', 'follow_up', 'converted', 'dropped'].map((status) => {
                                const checked = leadHeatmapStatuses.includes(status);
                                return (
                                    <label key={status} className={s.leadHeatChip}>
                                        <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                                if (!setLeadHeatmapStatuses) return;
                                                setLeadHeatmapStatuses((prev) => {
                                                    const next = new Set(prev || []);
                                                    if (next.has(status)) next.delete(status);
                                                    else next.add(status);
                                                    return Array.from(next);
                                                });
                                            }}
                                        />
                                        {status.replace('_', ' ')}
                                    </label>
                                );
                            })}
                        </div>
                        <div className={s.leadHeatLabel}>Tonnage</div>
                        <select
                            className="form-control form-control-sm mb-2"
                            value={leadTonnageMode}
                            onChange={(e) => setLeadTonnageMode?.(e.target.value)}
                        >
                            <option value="all">All destinations</option>
                            <option value="known">Only parsed tonnage</option>
                        </select>
                        <div className={s.leadHeatRange}>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                className="form-control form-control-sm"
                                placeholder="Min t"
                                value={leadTonnageMin}
                                onChange={(e) => setLeadTonnageMin?.(e.target.value)}
                            />
                            <span>–</span>
                            <input
                                type="number"
                                min="0"
                                step="0.1"
                                className="form-control form-control-sm"
                                placeholder="Max t"
                                value={leadTonnageMax}
                                onChange={(e) => setLeadTonnageMax?.(e.target.value)}
                            />
                        </div>
                        <div className={s.leadHeatHint}>
                            Tonnage is unlabeled when load text could not be parsed.
                            Distance is informational only.
                        </div>
                    </div>
                )}

                <div style={{ height: '1px', background: 'var(--bs-border-color, #f0f0f0)', margin: '6px 0' }} />

                {/* Vehicles */}
                <div className="d-flex align-items-center justify-content-between py-2">
                    <label
                        style={{ fontSize: '13px', color: 'var(--bs-body-color, #333)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                        htmlFor="lp-drivers"
                        onClick={() => setShowDrivers(v => !v)}
                    >
                        🚛 Vehicles
                    </label>
                    <ToggleSwitch
                        checked={showDrivers}
                        onChange={setShowDrivers}
                        id="lp-drivers"
                    />
                </div>

                {/* Vehicles sub-toggle: Registered Vehicles */}
                {showDrivers && (
                    <div style={{ paddingLeft: '18px', borderLeft: '2px solid var(--bs-border-color, #eee)', marginLeft: '4px', marginTop: '2px', marginBottom: '2px' }}>
                        <div className="d-flex align-items-center justify-content-between py-1">
                            <label
                                style={{
                                    fontSize: '12px',
                                    color: showRegisteredVehicles ? '#9ca3af' : 'var(--bs-secondary-color, #666)',
                                    fontWeight: showRegisteredVehicles ? 600 : 400,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    marginBottom: 0
                                }}
                                htmlFor="lp-registeredVehicles"
                                onClick={() => setShowRegisteredVehicles(v => !v)}
                            >
                                <span style={{ marginRight: '6px', color: '#9ca3af', fontSize: '10px' }}>⬤</span> Registered Vehicles
                            </label>
                            <ToggleSwitch
                                checked={showRegisteredVehicles}
                                onChange={setShowRegisteredVehicles}
                                id="lp-registeredVehicles"
                            />
                        </div>

                        <div className="d-flex align-items-center justify-content-between py-1">
                            <label
                                style={{
                                    fontSize: '12px',
                                    color: showTransportersOnly ? '#db2777' : 'var(--bs-secondary-color, #666)',
                                    fontWeight: showTransportersOnly ? 600 : 400,
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    marginBottom: 0
                                }}
                                htmlFor="lp-transportersOnly"
                                onClick={() => setShowTransportersOnly(v => !v)}
                            >
                                <span style={{ marginRight: '6px', color: showTransportersOnly ? '#db2777' : '#9ca3af', fontSize: '10px' }}>⬤</span> Only Transporters
                            </label>
                            <ToggleSwitch
                                checked={showTransportersOnly}
                                onChange={setShowTransportersOnly}
                                id="lp-transportersOnly"
                            />
                        </div>

                        {vehicleClassOptions.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                                <div style={{ fontSize: '12px', color: 'var(--bs-secondary-color, #666)', fontWeight: 600 }}>Vehicle Class</div>
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                                    gap: '6px',
                                    marginTop: '6px',
                                    maxHeight: '110px',
                                    overflowY: 'auto',
                                    paddingRight: '4px'
                                }}>
                                    <button
                                        type="button"
                                        style={{
                                            border: '1px solid #d1d5db',
                                            borderRadius: '6px',
                                            padding: '4px 8px',
                                            fontSize: '11px',
                                            background: vehicleClassFilter.length === 0 ? '#dbeafe' : '#fff',
                                            borderColor: vehicleClassFilter.length === 0 ? '#60a5fa' : '#d1d5db',
                                            color: vehicleClassFilter.length === 0 ? '#1d4ed8' : '#374151',
                                            fontWeight: vehicleClassFilter.length === 0 ? 600 : 400,
                                            cursor: !showDrivers ? 'not-allowed' : 'pointer',
                                            transition: 'all 0.15s',
                                            opacity: !showDrivers ? 0.5 : 1
                                        }}
                                        onClick={clearVehicleClassFilter}
                                        disabled={!showDrivers}
                                    >
                                        All
                                    </button>
                                    {vehicleClassOptions.map((classNum) => {
                                        const isActive = vehicleClassFilter.includes(classNum);
                                        return (
                                            <button
                                                key={classNum}
                                                type="button"
                                                style={{
                                                    border: '1px solid #d1d5db',
                                                    borderRadius: '6px',
                                                    padding: '4px 8px',
                                                    fontSize: '11px',
                                                    background: isActive ? '#dbeafe' : '#fff',
                                                    borderColor: isActive ? '#60a5fa' : '#d1d5db',
                                                    color: isActive ? '#1d4ed8' : '#374151',
                                                    fontWeight: isActive ? 600 : 400,
                                                    cursor: !showDrivers ? 'not-allowed' : 'pointer',
                                                    transition: 'all 0.15s',
                                                    opacity: !showDrivers ? 0.5 : 1
                                                }}
                                                onClick={() => toggleVehicleClass(classNum)}
                                                disabled={!showDrivers}
                                            >
                                                {classNum}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                <div style={{ height: '1px', background: 'var(--bs-border-color, #f0f0f0)', margin: '6px 0' }} />

                {/* Bookings */}
                <div className="d-flex align-items-center justify-content-between py-2">
                    <label
                        style={{ fontSize: '13px', color: 'var(--bs-body-color, #333)', cursor: 'pointer', userSelect: 'none', marginBottom: 0 }}
                        htmlFor="lp-bookings"
                        onClick={() => setShowBookings(v => !v)}
                    >
                        📦 Bookings
                    </label>
                    <ToggleSwitch
                        checked={showBookings}
                        onChange={setShowBookings}
                        id="lp-bookings"
                    />
                </div>

                {/* Bookings sub-toggles */}
                <div style={{ paddingLeft: '18px', borderLeft: '2px solid var(--bs-border-color, #eee)', marginLeft: '4px', marginTop: '2px', marginBottom: '2px' }}>
                    <div className="d-flex align-items-center justify-content-between py-1">
                        <label
                            style={{
                                fontSize: '12px',
                                color: !showBookings ? '#aaa' : (showCriticalOnly ? '#ef4444' : 'var(--bs-secondary-color, #666)'),
                                fontWeight: showCriticalOnly ? 600 : 400,
                                cursor: !showBookings ? 'not-allowed' : 'pointer',
                                userSelect: 'none',
                                marginBottom: 0
                            }}
                            htmlFor="lp-criticalOnly"
                            onClick={() => showBookings && setShowCriticalOnly(v => !v)}
                        >
                            ⚡ Critical Only
                        </label>
                        <ToggleSwitch
                            checked={showCriticalOnly}
                            onChange={setShowCriticalOnly}
                            id="lp-criticalOnly"
                            disabled={!showBookings}
                        />
                    </div>
                    <div className="d-flex align-items-center justify-content-between py-1">
                        <label
                            style={{
                                fontSize: '12px',
                                color: !showBookings ? '#aaa' : (showCompletedBookings ? '#10b981' : 'var(--bs-secondary-color, #666)'),
                                fontWeight: showCompletedBookings ? 600 : 400,
                                cursor: !showBookings ? 'not-allowed' : 'pointer',
                                userSelect: 'none',
                                marginBottom: 0
                            }}
                            htmlFor="lp-completed"
                            onClick={() => showBookings && setShowCompletedBookings(v => !v)}
                        >
                            ✅ Completed
                        </label>
                        <ToggleSwitch
                            checked={showCompletedBookings}
                            onChange={setShowCompletedBookings}
                            id="lp-completed"
                            disabled={!showBookings}
                        />
                    </div>
                </div>
            </div>

            {/* Serviceability quick action */}
            <div className={s.statCard} style={{ background: 'linear-gradient(135deg, #eef4ff 0%, #f6fafe 100%)' }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-1">Serviceable Regions</h5>
                        <div className="small text-muted">Toggle city/pincode availability</div>
                    </div>
                    <i className="fa fa-map-o" style={{ fontSize: '22px', color: '#3b82f6' }} />
                </div>
                <button
                    type="button"
                    className="btn btn-primary btn-sm mt-3 w-100"
                    onClick={onModifyServiceableRegions}
                >
                    <i className="fa fa-sliders mr-2" /> Modify Serviceable Regions
                </button>
            </div>

            {/* Address Management quick action */}
            <div className={s.statCard} style={{ background: 'linear-gradient(135deg, #f0fdf4 0%, #ecfdf5 100%)' }}>
                <div className="d-flex justify-content-between align-items-center">
                    <div>
                        <h5 className="mb-1">Manage Addresses</h5>
                        <div className="small text-muted">View, edit, or delete customer addresses</div>
                    </div>
                    <i className="fa fa-address-book" style={{ fontSize: '22px', color: '#10b981' }} />
                </div>
                <button
                    type="button"
                    className="btn btn-success btn-sm mt-3 w-100"
                    onClick={onModifyAddresses}
                >
                    <i className="fa fa-pencil mr-2" /> Manage Addresses
                </button>
            </div>
        </div >
    );
};

OpsMapStats.propTypes = {
    driverCount: PropTypes.number.isRequired,
    bookingCount: PropTypes.number.isRequired,
    timeWindow: PropTypes.string.isRequired,
    bookings: PropTypes.array,
    driversList: PropTypes.array,
    clusterDrivers: PropTypes.array,
    selectedDriver: PropTypes.object,
    onClearClusterDrivers: PropTypes.func,
    onClusterDriverClick: PropTypes.func,
    onDriverClick: PropTypes.func,
    onBookingClick: PropTypes.func,
    driverStatusFilter: PropTypes.string.isRequired,
    setDriverStatusFilter: PropTypes.func.isRequired,
    onModifyServiceableRegions: PropTypes.func,
    onModifyAddresses: PropTypes.func,
    pincodeAnalysis: PropTypes.object,
    onClearPincodeAnalysis: PropTypes.func,
    // Layer props
    showServiceZone: PropTypes.bool,
    setShowServiceZone: PropTypes.func,
    showHotzones: PropTypes.bool,
    setShowHotzones: PropTypes.func,
    showRealtimeHotzones: PropTypes.bool,
    setShowRealtimeHotzones: PropTypes.func,
    showHistoricalHotzones: PropTypes.bool,
    setShowHistoricalHotzones: PropTypes.func,
    showLeadHeatmap: PropTypes.bool,
    setShowLeadHeatmap: PropTypes.func,
    leadHeatmapStatuses: PropTypes.array,
    setLeadHeatmapStatuses: PropTypes.func,
    leadTonnageMode: PropTypes.string,
    setLeadTonnageMode: PropTypes.func,
    leadTonnageMin: PropTypes.string,
    setLeadTonnageMin: PropTypes.func,
    leadTonnageMax: PropTypes.string,
    setLeadTonnageMax: PropTypes.func,
    leadHeatmap: PropTypes.object,
    leadHeatmapLoading: PropTypes.bool,
    filteredLeadDestinationCount: PropTypes.number,
    showDrivers: PropTypes.bool,
    setShowDrivers: PropTypes.func,
    showRegisteredVehicles: PropTypes.bool,
    setShowRegisteredVehicles: PropTypes.func,
    vehicleClassOptions: PropTypes.array,
    vehicleClassFilter: PropTypes.array,
    setVehicleClassFilter: PropTypes.func,
    showBookings: PropTypes.bool,
    setShowBookings: PropTypes.func,
    showCriticalOnly: PropTypes.bool,
    setShowCriticalOnly: PropTypes.func,
    showCompletedBookings: PropTypes.bool,
    setShowCompletedBookings: PropTypes.func,
};

export default OpsMapStats;
